// Solving together. A room is one puzzle date shared by everyone holding
// its code; letters are written PER CELL, so two phones typing in different
// corners never overwrite each other, and the database merges them for
// free - no transactions, no sequence numbers, which is why this is so much
// simpler than the turn-based games' multiplayer.js.
//
//   crosswordRooms/<id>/meta       { date, code, hostUid, createdAt, startedAt,
//                                     banked, runFrom, activeAt (the clock: solving.js),
//                                     solvedAt, players: { uid: { name, color, joinedAt } } }
//   crosswordRooms/<id>/letters    { "r-c": { l: 'A', by: uid } }
//   crosswordRooms/<id>/presence   { uid: { row, col, dir, name, color, at } }
//   crosswordRooms/<id>/chat       { pushId: { uid, name, color, text, at } }  post-only
//   crosswordRoomCodes/<CODE>      roomId
//   crosswordUserRooms/<uid>/<id>  { date, at } - rooms I made or joined by
//                                   code, so home can offer them again
//
// A group's room (groups.js) is an ordinary room with `groupId` and
// `groupName` in its meta and the group's code as its code.
//
// Join links are `?join=CODE`, like every other hub game.
import {
  ref as dbRef, get, set, update, remove, push, onValue, onDisconnect, serverTimestamp,
  query, limitToLast,
} from 'firebase/database';
import { db } from '@/firebase';
import { generateRoomCode, normalizeRoomCode } from '@/utils/roomCodes';
import { SOLVER_COLORS, roomClockStart } from '@/engine/solving';
import { cleanChatText, chatList, CHAT_KEEP } from '@/utils/chat';

export const ROOMS_PATH = 'crosswordRooms';
export const CODES_PATH = 'crosswordRoomCodes';
export const USER_ROOMS_PATH = 'crosswordUserRooms';

const playerColor = (players) => SOLVER_COLORS[Object.keys(players || {}).length % SOLVER_COLORS.length];

/** Claim a fresh room code for a room id. */
export async function claimRoomCode(roomId) {
  for (let attempt = 0; attempt < 8; attempt += 1) {
    const candidate = generateRoomCode(attempt);
    // A group's code is never handed to a one-day room: the join box
    // tries groups first and would never find it.
    // eslint-disable-next-line no-await-in-loop -- one try at a time, in order
    if ((await get(dbRef(db, `crosswordGroupCodes/${candidate}`))).exists()) continue;
    // Create-only rule on the code node: a taken code refuses the write.
    try {
      // eslint-disable-next-line no-await-in-loop
      await set(dbRef(db, `${CODES_PATH}/${candidate}`), roomId);
      return candidate;
    } catch { /* taken; try another */ }
  }
  throw new Error('Could not claim a room code.');
}

/**
 * Create a room for a date, seeded with the creator's letters so far, and
 * claim a code for it. Returns { roomId, code }.
 */
export async function createRoom({ date, user, letters, seconds }) {
  if (!db) throw new Error('Rooms need Firebase configured.');
  const roomRef = push(dbRef(db, ROOMS_PATH));
  const roomId = roomRef.key;
  const code = await claimRoomCode(roomId);
  const name = user.displayName || user.email || 'Someone';
  const seeded = {};
  for (const [k, v] of Object.entries(letters || {})) {
    const l = typeof v === 'string' ? v : v?.l;
    if (l) seeded[k] = { l, by: user.uid };
  }
  // Two paths in one atomic update, not one set at the room root: the rules
  // grant `meta` and `letters` separately and nothing at the room node
  // itself, and a write is checked at the location it is made.
  const now = Date.now();
  await update(dbRef(db), {
    [`${ROOMS_PATH}/${roomId}/meta`]: {
      date,
      code,
      hostUid: user.uid,
      createdAt: serverTimestamp(),
      // The shared clock starts now, minus whatever the creator already
      // spent - so a puzzle shared ten minutes in shows ten minutes.
      startedAt: now - (seconds || 0) * 1000,
      ...roomClockStart(seconds, now),
      solvedAt: null,
      players: { [user.uid]: { name, color: SOLVER_COLORS[0], joinedAt: serverTimestamp() } },
    },
    [`${ROOMS_PATH}/${roomId}/letters`]: seeded,
  });
  return { roomId, code };
}

/** Resolve a code to a room id, or null. */
export async function findRoomByCode(input) {
  const code = normalizeRoomCode(input);
  if (!code || !db) return null;
  const snap = await get(dbRef(db, `${CODES_PATH}/${code}`));
  return snap.exists() ? { roomId: snap.val(), code } : null;
}

/**
 * Add me to the room's players (idempotent). A group's room passes my
 * group colour, so I am the same colour every day.
 */
export async function joinRoom(roomId, user, { color } = {}) {
  const metaSnap = await get(dbRef(db, `${ROOMS_PATH}/${roomId}/meta`));
  if (!metaSnap.exists()) throw new Error('That room no longer exists.');
  const meta = metaSnap.val();
  if (!meta.players?.[user.uid]) {
    await update(dbRef(db, `${ROOMS_PATH}/${roomId}/meta/players/${user.uid}`), {
      name: user.displayName || user.email || 'Someone',
      color: color || playerColor(meta.players),
      joinedAt: serverTimestamp(),
    });
  }
  return meta;
}

/**
 * Live-bind a room: meta, letters and presence. Returns an unbind function.
 * Presence for me is written by `writePresence` and removed on disconnect.
 */
export function bindRoom(roomId, uid, {
  onMeta, onLetters, onPresence, onChat,
}) {
  const base = `${ROOMS_PATH}/${roomId}`;
  const offs = [
    onValue(dbRef(db, `${base}/meta`), (s) => onMeta(s.val())),
    onValue(dbRef(db, `${base}/letters`), (s) => onLetters(s.val() || {})),
    onValue(dbRef(db, `${base}/presence`), (s) => onPresence(s.val() || {})),
  ];
  if (onChat) {
    offs.push(onValue(query(dbRef(db, `${base}/chat`), limitToLast(CHAT_KEEP)), (s) => onChat(chatList(s.val())), (error) => {
      console.error('crossword: room chat read failed', error);
    }));
  }
  const mine = dbRef(db, `${base}/presence/${uid}`);
  onDisconnect(mine).remove().catch(() => {});
  return () => {
    offs.forEach((off) => off());
    remove(mine).catch(() => {});
  };
}

export function writeLetter(roomId, cellKey, letter, uid) {
  const cell = dbRef(db, `${ROOMS_PATH}/${roomId}/letters/${cellKey}`);
  return (letter ? set(cell, { l: letter, by: uid }) : remove(cell)).catch((error) => {
    console.error('crossword: room letter write failed', error);
  });
}

export function writeLetters(roomId, entries, uid) {
  const patch = {};
  for (const [cellKey, letter] of entries) {
    patch[`${ROOMS_PATH}/${roomId}/letters/${cellKey}`] = letter ? { l: letter, by: uid } : null;
  }
  return update(dbRef(db), patch).catch((error) => {
    console.error('crossword: room letters write failed', error);
  });
}

let lastPresence = '';
export function writePresence(roomId, uid, presence) {
  const serialized = JSON.stringify([roomId, presence.row, presence.col, presence.dir]);
  if (serialized === lastPresence) return;
  lastPresence = serialized;
  set(dbRef(db, `${ROOMS_PATH}/${roomId}/presence/${uid}`), { ...presence, at: Date.now() }).catch(() => {});
}

/** Post to the room's chat. Resolves false if there was nothing to send or it failed. */
export async function sendChat(roomId, { uid, name, color }, text) {
  const clean = cleanChatText(text);
  if (!clean || !roomId || !db) return false;
  try {
    await set(push(dbRef(db, `${ROOMS_PATH}/${roomId}/chat`)), {
      uid,
      name: (name || 'Someone').slice(0, 60),
      color: color || SOLVER_COLORS[0],
      text: clean,
      at: serverTimestamp(),
    });
    return true;
  } catch (error) {
    console.error('crossword: chat send failed', error);
    return false;
  }
}

/** A tap or key's change to the room's clock (solving.js roomClockPatch). */
export function writeRoomClock(roomId, patch) {
  return update(dbRef(db, `${ROOMS_PATH}/${roomId}/meta`), patch).catch((error) => {
    console.error('crossword: room clock write failed', error);
  });
}

export function markSolved(roomId) {
  return set(dbRef(db, `${ROOMS_PATH}/${roomId}/meta/solvedAt`), Date.now()).catch(() => {});
}

/**
 * Remember a room I'm in (Matt 2026-10-27: a Giant shared with a friend
 * vanished from home, because only a group's room had a button). Group
 * rooms aren't noted: the group's own buttons find them.
 */
export function noteMyRoom(uid, roomId, date) {
  if (!db || !uid || !roomId || !date) return Promise.resolve();
  return set(dbRef(db, `${USER_ROOMS_PATH}/${uid}/${roomId}`), { date, at: serverTimestamp() }).catch((error) => {
    console.error('crossword: noting my room failed', error);
  });
}

/** My noted rooms, live: `onRooms({ roomId: { date, at } })`. Returns an unbind function. */
export function watchMyRooms(uid, onRooms) {
  return onValue(dbRef(db, `${USER_ROOMS_PATH}/${uid}`), (s) => onRooms(s.val() || {}), () => onRooms({}));
}

/** One room's meta, live. Returns an unbind function. */
export function watchRoomMeta(roomId, onMeta) {
  return onValue(dbRef(db, `${ROOMS_PATH}/${roomId}/meta`), (s) => onMeta(s.val()), () => onMeta(null));
}

export const joinLink = (code) => `${window.location.origin}/?join=${code}`;
